import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../app/providers/AuthProvider';
import { UserSidebarLayout } from '../components/UserSidebarLayout';
import { COLORS } from '../../../core/constants/colors';
import { QueryRecommendationsSection } from '../../recommendations/components/QueryRecommendationsSection';
import { useChatSession } from '../../chat/hooks/useChatSession';
import { MessageMarkdown } from '../../chat/components/MessageMarkdown';
import { ShareChatDrawer } from '../../chat/components/ShareChatDrawer';
import type { SourceChunk } from '../../chat/types';

export const ChatPage: React.FC = () => {
    const { conversationId } = useParams<{ conversationId: string }>();
    const location = useLocation();
    const navigate = useNavigate();
    const { authState } = useAuth();

    const {
        messages,
        isLoading,
        input,
        setInput,
        messagesEndRef,
        sendMessage,
        startNewConversation,
    } = useChatSession({
        initialConversationId: conversationId ?? null,
        // Scope a new conversation to the company passed from the company page.
        companyId: (location.state as { companyId?: number } | null)?.companyId ?? null,
        onConversationCreated: (newId) => {
            if (!conversationId || conversationId !== newId) {
                navigate(`/dashboard/chat/${newId}`, { replace: true });
            }
        },
    });

    const [dismissedRecommendations, setDismissedRecommendations] = useState<Set<number>>(new Set());
    const [copiedMessageId, setCopiedMessageId] = useState<string | null>(null);
    const [showFAQs, setShowFAQs] = useState(false);
    const [shareDrawerOpen, setShareDrawerOpen] = useState(false);
    const hasProcessedPendingQuery = useRef(false);

    // Calculate if we should show recommendations (every 3 user queries)
    const { shouldShowRecommendations, recentQueries, queryCount } = useMemo(() => {
        const userMessages = messages.filter(m => m.role === 'user' && m.status === 'success');
        const count = userMessages.length;
        const shouldShow = count > 0 && count % 3 === 0 && !dismissedRecommendations.has(count);
        const recent = userMessages.slice(-3).map(m => m.content);
        return {
            shouldShowRecommendations: shouldShow,
            recentQueries: recent,
            queryCount: count,
        };
    }, [messages, dismissedRecommendations]);

    // Reset state when navigating to new chat (no conversationId)
    useEffect(() => {
        if (!conversationId) {
            startNewConversation();
            hasProcessedPendingQuery.current = false;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [conversationId, location.state?.newChat]);

    // Handle pending query from navigation state
    useEffect(() => {
        if (location.state?.pendingQuery && location.state?.isNewQuery && !hasProcessedPendingQuery.current) {
            hasProcessedPendingQuery.current = true;
            const queryText = location.state.pendingQuery as string;
            setInput(queryText);
            window.history.replaceState({}, document.title);
            setTimeout(() => {
                const form = document.querySelector('form');
                if (form) form.requestSubmit();
            }, 100);
        }
    }, [location.state, setInput]);

    const handleSendMessage = async (e?: React.FormEvent) => {
        e?.preventDefault();
        await sendMessage();
    };

    const handleCopyMessage = async (messageId: string, content: string) => {
        try {
            await navigator.clipboard.writeText(content);
            setCopiedMessageId(messageId);
            setTimeout(() => setCopiedMessageId(null), 2000);
        } catch (err) {
            console.error('Failed to copy:', err);
        }
    };

    const getUserInitial = () => {
        return authState.user?.full_name?.charAt(0).toUpperCase() || 'U';
    };

    return (
        <UserSidebarLayout>
            {/* Main Chat Area */}
            <div className="flex-1 overflow-hidden flex flex-col w-full h-screen bg-white" style={{ fontFamily: 'DM Sans, sans-serif' }}>

                {/* Messages List */}
                <div className="flex-1 overflow-y-auto p-6 space-y-6">
                    {messages.length === 0 && !isLoading && (
                        <div className="h-full flex flex-col items-center justify-center text-gray-400 min-h-[60vh]">
                            <div className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center mb-4">
                                <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                                </svg>
                            </div>
                            <p className="text-lg font-medium">Start a new conversation</p>
                        </div>
                    )}

                    {messages.map((msg) => (
                        <div key={msg.id}>
                            {msg.role === 'assistant' ? (
                                <div className="flex items-start gap-3">
                                    {/* AI Icon with Blue Lightning Bolt */}
                                    <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: COLORS.user.primaryLight }}>
                                        <svg className="w-5 h-5" style={{ color: COLORS.user.primary }} fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" />
                                        </svg>
                                    </div>
                                    <div className="flex-1 max-w-[80%]">
                                        {/* AI Message Bubble */}
                                        <div className="rounded-2xl p-5 shadow-sm" style={{ backgroundColor: '#FFFFFF', border: '1px solid #E5E7EB' }}>
                                            {msg.status === 'sending' && !msg.content ? (
                                                <div className="flex items-center gap-3">
                                                    <span className="text-sm font-medium text-gray-700">Thinking...</span>
                                                    <div className="flex items-center gap-1.5">
                                                        <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                                                        <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                                                        <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                                                    </div>
                                                </div>
                                            ) : msg.status === 'error' ? (
                                                <div className="text-red-600 text-sm">{msg.content}</div>
                                            ) : (
                                            <div className="leading-relaxed text-gray-800 prose prose-sm max-w-none prose-headings:text-gray-900 prose-headings:font-semibold prose-headings:mt-4 prose-headings:mb-2 prose-p:my-2 prose-ul:my-2 prose-ol:my-2 prose-li:my-0.5 prose-table:text-sm prose-th:bg-gray-50 prose-th:px-3 prose-th:py-2 prose-td:px-3 prose-td:py-2 prose-hr:my-3 prose-blockquote:border-l-blue-400 prose-blockquote:bg-blue-50 prose-blockquote:py-1 prose-blockquote:px-4 prose-blockquote:rounded-r-lg prose-a:text-blue-600 prose-strong:text-gray-900">
                                                <MessageMarkdown content={msg.content} />
                                            </div>
                                            )}

                                            {/* Sources Section */}
                                            {msg.sources && msg.sources.length > 0 && (
                                                <div className="mt-4 pt-4 border-t border-gray-300">
                                                    <div className="flex items-center gap-2 mb-3">
                                                        <svg className="w-4 h-4" style={{ color: COLORS.user.primary }} fill="currentColor" viewBox="0 0 20 20">
                                                            <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                                                        </svg>
                                                        <h4 className="font-semibold text-sm text-gray-800">
                                                            Sources
                                                        </h4>
                                                    </div>
                                                    <div className="space-y-2">
                                                        {msg.sources.map((source: SourceChunk, idx: number) => (
                                                            <div
                                                                key={idx}
                                                                className="p-3 rounded-lg bg-white border border-gray-200 hover:bg-gray-50 transition-colors"
                                                            >
                                                                <div className="flex items-start justify-between gap-2 mb-1">
                                                                    <div className="flex items-center gap-2">
                                                                        <span className="flex items-center justify-center w-5 h-5 rounded-full text-xs font-semibold text-white" style={{ backgroundColor: COLORS.user.primary }}>
                                                                            {idx + 1}
                                                                        </span>
                                                                        {source.document_name && (
                                                                            <span className="text-xs font-medium text-gray-900">
                                                                                {source.document_name}
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                    <span className="text-xs text-gray-500 flex-shrink-0">
                                                                        {Math.round(source.relevance_score * 100)}%
                                                                    </span>
                                                                </div>
                                                                <p className="text-xs text-gray-600 line-clamp-2">
                                                                    {source.chunk_text}
                                                                </p>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                        {/* Action Buttons */}
                                        {msg.status !== 'sending' && msg.content && (
                                        <div className="flex items-center gap-4 mt-2">
                                            <button
                                                onClick={() => handleCopyMessage(msg.id, msg.content)}
                                                className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                                            >
                                                {copiedMessageId === msg.id ? 'Copied!' : 'Copy'}
                                            </button>
                                        </div>
                                        )}
                                    </div>
                                </div>
                            ) : (
                                <div className="flex items-start gap-3 justify-end">
                                    <div className="flex-1 max-w-[80%] flex justify-end">
                                        <div className="bg-white rounded-2xl p-4 shadow-sm">
                                            <div className="whitespace-pre-wrap leading-relaxed text-gray-800">
                                                {msg.content}
                                            </div>
                                        </div>
                                    </div>
                                    {/* User Avatar */}
                                    <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-white text-sm font-semibold" style={{ backgroundColor: COLORS.user.primary }}>
                                        {getUserInitial()}
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}


                    {/* Show recommendations every 3 queries */}
                    {shouldShowRecommendations && recentQueries.length > 0 && (
                        <QueryRecommendationsSection
                            queries={recentQueries}
                            onCompanyClick={(id) => navigate(`/dashboard/companies/${id}`)}
                            onDismiss={() => {
                                setDismissedRecommendations(prev => new Set(prev).add(queryCount));
                            }}
                        />
                    )}


                    <div ref={messagesEndRef} />
                </div>

                {/* Input Area */}
                <div className="p-4 border-t border-gray-200 bg-white">
                    <div className="flex justify-center">
                        <form onSubmit={handleSendMessage} className="relative w-full max-w-4xl">
                        <input
                            type="text"
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            placeholder="Ask or search anything"
                                className="w-full pl-6 pr-16 py-4 rounded-xl border-2 focus:outline-none focus:border-purple-500 transition-colors"
                            style={{
                                backgroundColor: '#F5F5F5',
                                borderColor: isLoading ? '#eee' : '#E5E7EB'
                            }}
                            disabled={isLoading}
                        />
                        <button
                            type="submit"
                            disabled={!input.trim() || isLoading}
                                className="absolute right-2 top-1/2 transform -translate-y-1/2 w-10 h-10 rounded-full flex items-center justify-center transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                style={{
                                    backgroundColor: '#1F1F1F',
                                    color: '#FFFFFF'
                                }}
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                            </svg>
                        </button>
                    </form>
                        </div>
                    <div className="flex items-center justify-center gap-2 mt-3">
                        <button
                            onClick={() => setShowFAQs(true)}
                            className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-1.5"
                        >
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            FAQs
                            </button>
                        {conversationId && (
                            <button
                                onClick={() => setShareDrawerOpen(true)}
                                className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-1.5"
                            >
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12s-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                                </svg>
                                Share
                            </button>
                        )}
                    </div>
                </div>

                <ShareChatDrawer
                    isOpen={shareDrawerOpen}
                    onClose={() => setShareDrawerOpen(false)}
                    conversationId={conversationId ? Number(conversationId) : null}
                />

                {/* FAQs Modal */}
                {showFAQs && (
                    <div
                        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
                        onClick={() => setShowFAQs(false)}
                    >
                        <div
                            className="bg-white rounded-2xl shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
                                <h2 className="text-2xl font-bold text-gray-900">Frequently Asked Questions</h2>
                                <button
                                    onClick={() => setShowFAQs(false)}
                                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                                >
                                    <svg className="w-6 h-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                            </div>
                            <div className="p-6 space-y-6">
                                {[
                                    {
                                        question: "How does the AI-powered portfolio analysis work?",
                                        answer: "Our system uses advanced natural language processing to analyze your portfolio documents, extract key insights, and provide intelligent responses to your queries about company performance, financials, and trends."
                                    },
                                    {
                                        question: "What types of documents can I upload?",
                                        answer: "You can upload financial reports, pitch decks, business plans, legal documents, and any PDF files related to your portfolio companies. Our system automatically indexes and processes them for quick retrieval."
                                    },
                                    {
                                        question: "How secure is my portfolio data?",
                                        answer: "All data is encrypted at rest and in transit. We use enterprise-grade security measures including AWS S3 for storage, role-based access control, and regular security audits to protect your sensitive information."
                                    },
                                    {
                                        question: "Can I track multiple companies simultaneously?",
                                        answer: "Yes! The platform is designed to manage multiple portfolio companies. You can filter queries by specific companies, compare metrics across your portfolio, and get consolidated insights."
                                    },
                                    {
                                        question: "How accurate are the AI-generated insights?",
                                        answer: "Our AI system uses state-of-the-art language models trained on financial and business documents. While we strive for accuracy, always verify critical insights with original source documents and consult with your team for important decisions."
                                    },
                                    {
                                        question: "Can I export the analysis results?",
                                        answer: "Yes, you can export query results and analysis in various formats. Use the copy button on any response to save it, or access the full history page to export multiple queries at once."
                                    }
                                ].map((faq, index) => (
                                    <div key={index} className="pb-6 border-b border-gray-100 last:border-0">
                                        <h3 className="text-lg font-semibold text-gray-900 mb-2">
                                            {faq.question}
                                        </h3>
                                        <p className="text-gray-600 leading-relaxed">
                                            {faq.answer}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </UserSidebarLayout>
    );
};
